import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { CompanyResearchActionCards } from "../components/CompanyResearchActionCards";
import { CompanyResearchLoadingNotice } from "../components/CompanyResearchLoadingNotice";
import { CompanyResearchMarkdown, normalizeCompanyResearchText } from "../components/CompanyResearchMarkdown";
import {
  CompanyResearchSelfUrlModal,
  shouldPromptForSelfCompanyUrl,
} from "../components/CompanyResearchSelfUrlModal";
import type {
  CompanyResearchAvailableAction,
  CompanyResearchIntentType,
  CompanyResearchJobDetail,
  CompanyResearchJobError,
  CompanyResearchJobStatus,
  CompanyResearchRecordDetail,
} from "../lib/companyResearch";
import { companyResearchClient } from "../lib/companyResearchClient";
import { AppError } from "../lib/errors";

const COMPANY_RESEARCH_DETAIL_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const DEFAULT_ACTIONS: CompanyResearchAvailableAction[] = [
  { action_type: "learn_more", label: "詳しく調べる" },
  { action_type: "sales", label: "売り込みたい" },
  { action_type: "partnership", label: "協業したい" },
  { action_type: "risk_check", label: "リスクを確認したい" },
];

function isPendingStatus(status: CompanyResearchJobStatus) {
  return status === "accepted" || status === "running";
}

function hasPendingJobs(record: CompanyResearchRecordDetail) {
  return (record.deep_research_jobs ?? []).some((job) => isPendingStatus(job.status));
}

function getRunningIntentTypes(jobs: CompanyResearchJobDetail[]) {
  return jobs
    .filter((job): job is CompanyResearchJobDetail & { intent_type: CompanyResearchIntentType } =>
      Boolean(job.intent_type && isPendingStatus(job.status))
    )
    .map((job) => job.intent_type);
}

function mergeIntentTypes(...groups: CompanyResearchIntentType[][]) {
  return Array.from(new Set(groups.flat()));
}

function hasJobForIntent(jobs: CompanyResearchJobDetail[] | undefined, intentType: CompanyResearchIntentType) {
  return Boolean(jobs?.some((job) => job.intent_type === intentType));
}

function getPollDelayMs(startedAt: number) {
  const elapsedMs = Date.now() - startedAt;
  if (elapsedMs < 10_000) return 2_000;
  if (elapsedMs < 120_000) return 5_000;
  return 10_000;
}

function createErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "企業調査履歴の取得に失敗しました。時間をおいて再試行してください。";
}

function formatAdditionalAnalysisText(value: string) {
  return normalizeCompanyResearchText(value.replace(/詳細調査/g, "追加分析"));
}

function formatJobError(error: CompanyResearchJobDetail["error"]) {
  if (!error) return "追加分析に失敗しました。時間をおいて再実行してください。";
  if (typeof error === "string") return formatAdditionalAnalysisText(error);
  const typedError = error as CompanyResearchJobError;
  const message = typedError.user_message || typedError.message || typedError.code || "追加分析に失敗しました。";
  return formatAdditionalAnalysisText(message);
}

function formatDate(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function jobStatusLabel(status: CompanyResearchJobStatus) {
  if (status === "completed") return "完了";
  if (status === "failed") return "失敗";
  if (status === "needs_more_info") return "追加情報が必要";
  if (status === "ambiguous") return "候補確認が必要";
  if (status === "accepted") return "受付済み";
  return "調査中";
}

function intentLabel(intent?: CompanyResearchIntentType) {
  if (intent === "sales") return "売り込みたい";
  if (intent === "partnership") return "協業したい";
  if (intent === "risk_check") return "リスクを確認したい";
  return "詳しく調べる";
}

function getJobTime(job: CompanyResearchJobDetail) {
  const value = job.completed_at || job.updated_at || job.created_at;
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function getLatestCompletedJobId(jobs: CompanyResearchJobDetail[]) {
  return jobs
    .filter((job) => job.status === "completed")
    .sort((a, b) => getJobTime(b) - getJobTime(a))[0]?.job_id;
}

function getDefaultOpenJobIds(jobs: CompanyResearchJobDetail[]) {
  const latestCompletedJobId = getLatestCompletedJobId(jobs);
  return [
    ...(latestCompletedJobId ? [latestCompletedJobId] : []),
    ...jobs.filter((job) => job.status === "failed" || job.status === "needs_more_info").map((job) => job.job_id),
  ];
}

function createReportExcerpt(value: string) {
  const plain = value
    .replace(/^#+\s*/gm, "")
    .replace(/[-*]\s+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 120 ? `${plain.slice(0, 120)}...` : plain;
}

function createQuickSummaryPayload(record: CompanyResearchRecordDetail) {
  return {
    status: record.status,
    quick_summary_id: record.quick_summary_id,
    summary_level: record.summary_level ?? "quick",
    display_text: record.display_text,
    structured_result: record.structured_result,
    usage_estimate: record.usage_estimate,
  };
}

function JobStatusBadge({ status }: { status: CompanyResearchJobStatus }) {
  return <span className="rounded-full bg-[#F6E7DA] px-3 py-1 text-xs font-black text-[#7C5A4E]">{jobStatusLabel(status)}</span>;
}

function QuickSummaryPanel({ record }: { record: CompanyResearchRecordDetail }) {
  return (
    <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
      {record.display_text ? (
        <CompanyResearchMarkdown text={record.display_text} />
      ) : (
        <p className="text-sm leading-6 text-[#6B5851]">簡易概要を取得できませんでした。</p>
      )}
    </section>
  );
}

function AdditionalActionsPanel({
  loadingAction,
  jobs,
  startedIntentTypes,
  optimisticPendingIntentTypes,
  onCreateJob,
}: {
  loadingAction: CompanyResearchIntentType | null;
  jobs: CompanyResearchJobDetail[];
  startedIntentTypes: CompanyResearchIntentType[];
  optimisticPendingIntentTypes: CompanyResearchIntentType[];
  onCreateJob: (intentType: CompanyResearchIntentType) => void;
}) {
  const runningIntentTypes = mergeIntentTypes(getRunningIntentTypes(jobs), optimisticPendingIntentTypes);

  return (
    <section className="rounded-2xl border border-[#EADBC8] bg-[#FFFCFA] p-5">
      <h2 className="text-sm font-black text-[#5D4D46]">追加分析</h2>
      <p className="mt-2 text-xs font-bold leading-5 text-[#8A746A]">
        この確認事項をもとに、次の分析へ進めます。
      </p>
      <CompanyResearchActionCards
        actions={DEFAULT_ACTIONS}
        loadingAction={loadingAction}
        runningIntentTypes={runningIntentTypes}
        lockedIntentTypes={startedIntentTypes}
        onCreateJob={onCreateJob}
      />
    </section>
  );
}

function DeepResearchJobCard({
  job,
  isOpen,
  onToggle,
}: {
  job: CompanyResearchJobDetail;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const reportText = job.display_text || job.report_text || job.short_summary || "";
  const isAlwaysOpen = isPendingStatus(job.status);
  const bodyOpen = isOpen || isAlwaysOpen;
  const excerpt = reportText ? createReportExcerpt(formatAdditionalAnalysisText(reportText)) : "";

  return (
    <article className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-black text-[#5D4D46]">{intentLabel(job.intent_type)}</h3>
          <p className="mt-1 text-xs font-bold text-[#9C867D]">
            作成: {formatDate(job.created_at)} / 完了: {formatDate(job.completed_at)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <JobStatusBadge status={job.status} />
          <button
            type="button"
            onClick={onToggle}
            disabled={isAlwaysOpen}
            aria-expanded={bodyOpen}
            className="rounded-full border border-[#D8C0AF] bg-white px-3 py-1 text-xs font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-default disabled:border-[#EADBC8] disabled:text-[#9C867D]"
          >
            {isAlwaysOpen ? "表示中" : bodyOpen ? "閉じる" : "開く"}
          </button>
        </div>
      </div>

      {!bodyOpen && excerpt && (
        <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
          <span className="font-black text-[#5D4D46]">概要抜粋: </span>
          {excerpt}
        </p>
      )}

      {bodyOpen && job.status === "completed" ? (
        <div className="mt-4 rounded-2xl border border-[#F2E6DA] bg-[#FFFCFA] p-4">
          {reportText ? (
            <CompanyResearchMarkdown text={formatAdditionalAnalysisText(reportText)} />
          ) : (
            <p className="text-sm leading-6 text-[#6B5851]">追加分析レポートを取得できませんでした。</p>
          )}
        </div>
      ) : bodyOpen && (job.status === "failed" || job.status === "needs_more_info") ? (
        <p className="mt-3 break-words text-sm font-bold leading-6 text-[#B4533A] [overflow-wrap:anywhere]">
          {formatJobError(job.error)}
        </p>
      ) : bodyOpen ? (
        <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
          {formatAdditionalAnalysisText(job.message || "追加分析を実行しています。完了までしばらくお待ちください。")}
        </p>
      ) : null}
    </article>
  );
}

function DeepResearchJobsPanel({ jobs }: { jobs: CompanyResearchJobDetail[] }) {
  const [openJobIds, setOpenJobIds] = useState<string[]>(() => getDefaultOpenJobIds(jobs));

  useEffect(() => {
    setOpenJobIds((current) => {
      const jobIds = new Set(jobs.map((job) => job.job_id));
      const filtered = current.filter((jobId) => jobIds.has(jobId));
      const defaultOpenJobIds = getDefaultOpenJobIds(jobs);
      const hasOpenCompleted = filtered.some((jobId) => jobs.find((job) => job.job_id === jobId)?.status === "completed");
      const nextDefaultOpenJobIds = defaultOpenJobIds.filter((jobId) => {
        const job = jobs.find((item) => item.job_id === jobId);
        return job?.status !== "completed" || !hasOpenCompleted;
      });
      const next = [...filtered, ...nextDefaultOpenJobIds];
      return Array.from(new Set(next));
    });
  }, [jobs]);

  const toggleJob = (jobId: string) => {
    setOpenJobIds((current) =>
      current.includes(jobId) ? current.filter((value) => value !== jobId) : [...current, jobId]
    );
  };

  return (
    <section className="rounded-2xl border border-[#EADBC8] bg-white/80 p-5">
      <h2 className="text-sm font-black text-[#5D4D46]">追加分析履歴</h2>
      {jobs.length === 0 ? (
        <p className="mt-3 text-sm leading-6 text-[#6B5851]">まだ追加分析はありません。</p>
      ) : (
        <div className="mt-3 grid gap-3">
          {jobs.map((job) => (
            <DeepResearchJobCard
              key={job.job_id}
              job={job}
              isOpen={openJobIds.includes(job.job_id)}
              onToggle={() => toggleJob(job.job_id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function RequestCompanyResearchJobDetailPage() {
  const navigate = useNavigate();
  const { jobId: quickSummaryId } = useParams<{ jobId: string }>();
  const [record, setRecord] = useState<CompanyResearchRecordDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<CompanyResearchIntentType | null>(null);
  const [startedIntentTypes, setStartedIntentTypes] = useState<CompanyResearchIntentType[]>([]);
  const [optimisticPendingIntentTypes, setOptimisticPendingIntentTypes] = useState<CompanyResearchIntentType[]>([]);
  const [pendingSelfCompanyIntent, setPendingSelfCompanyIntent] = useState<CompanyResearchIntentType | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollTimerRef = useRef<number | null>(null);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    let active = true;
    startedAtRef.current = Date.now();

    const stopPolling = () => {
      if (pollTimerRef.current !== null) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const loadRecord = async () => {
      if (!quickSummaryId) {
        setErrorMessage("企業調査IDを確認できませんでした。");
        setLoading(false);
        return;
      }

      try {
        const nextRecord = await companyResearchClient.getRecord(quickSummaryId);
        if (!active) return;

        setRecord(nextRecord);
        setOptimisticPendingIntentTypes((values) =>
          values.filter((intentType) => !hasJobForIntent(nextRecord.deep_research_jobs, intentType))
        );
        setErrorMessage(null);
        setLoading(false);

        if (hasPendingJobs(nextRecord)) {
          if (Date.now() - startedAtRef.current >= COMPANY_RESEARCH_DETAIL_POLL_TIMEOUT_MS) {
            setErrorMessage("追加分析の完了確認に時間がかかっています。しばらくしてから再度ご確認ください。");
            return;
          }
          pollTimerRef.current = window.setTimeout(() => {
            void loadRecord();
          }, getPollDelayMs(startedAtRef.current));
        }
      } catch (error) {
        if (!active) return;
        if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        setErrorMessage(createErrorMessage(error));
        setLoading(false);
      }
    };

    void loadRecord();

    return () => {
      active = false;
      stopPolling();
    };
  }, [navigate, quickSummaryId, refreshKey]);

  const createAdditionalAnalysis = async (intentType: CompanyResearchIntentType, selfCompanyUrl?: string | null) => {
    if (!record || loadingAction) return;
    const disabledIntentTypes = mergeIntentTypes(getRunningIntentTypes(jobs), startedIntentTypes, optimisticPendingIntentTypes);
    if (disabledIntentTypes.includes(intentType)) return;

    setLoadingAction(intentType);
    setStartedIntentTypes((values) => mergeIntentTypes(values, [intentType]));
    setOptimisticPendingIntentTypes((values) => mergeIntentTypes(values, [intentType]));
    setErrorMessage(null);
    setStatusMessage(null);

    try {
      await companyResearchClient.createJob({
        quick_summary_id: record.quick_summary_id,
        quick_summary: createQuickSummaryPayload(record),
        intent_type: intentType,
        company_name: record.company_name,
        company_url: record.company_url,
        self_company_url: selfCompanyUrl?.trim() || null,
        email_delivery: false,
      });

      setStatusMessage("追加分析を開始しました。状態を更新しています。");
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setStartedIntentTypes((values) => values.filter((value) => value !== intentType));
      setOptimisticPendingIntentTypes((values) => values.filter((value) => value !== intentType));
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setErrorMessage(createErrorMessage(error));
    } finally {
      setLoadingAction(null);
    }
  };

  const jobs = record?.deep_research_jobs ?? [];

  const requestAdditionalAnalysis = (intentType: CompanyResearchIntentType) => {
    if (shouldPromptForSelfCompanyUrl(intentType)) {
      setPendingSelfCompanyIntent(intentType);
      return;
    }
    void createAdditionalAnalysis(intentType, null);
  };

  const submitSelfCompanyUrl = (selfCompanyUrl: string | null) => {
    const intentType = pendingSelfCompanyIntent;
    setPendingSelfCompanyIntent(null);
    if (!intentType) return;
    void createAdditionalAnalysis(intentType, selfCompanyUrl);
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request/company-research" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 企業リサーチへ
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">企業調査詳細</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">企業リサーチ</p>
              <h1 className="mt-3 break-words text-2xl font-black tracking-tight text-[#7C5A4E] [overflow-wrap:anywhere]">
                {record?.company_name || "企業調査詳細"}
              </h1>
              {record?.company_url && (
                <a
                  href={record.company_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 block break-all text-sm font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
                >
                  {record.company_url}
                </a>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Link
                to="/request/company-research/jobs"
                className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
              >
                調査履歴を見る
              </Link>
              <Link
                to="/request/company-research"
                state={{ startNew: true }}
                className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
              >
                新規リサーチ
              </Link>
            </div>
          </div>
          {statusMessage && <p className="mt-4 rounded-2xl bg-[#F4FAF1] p-3 text-sm font-bold text-[#4D6A48]">{statusMessage}</p>}
          {errorMessage && <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>}
        </section>

        <section
          aria-label="企業リサーチ詳細"
          className="mt-5 min-h-[360px] rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {loading && !record ? (
            <CompanyResearchLoadingNotice title="企業調査履歴を読み込んでいます。" />
          ) : record ? (
            <div className="space-y-4">
              <QuickSummaryPanel record={record} />
              {loadingAction && <CompanyResearchLoadingNotice title="詳細調査を開始しています..." />}
              {(hasPendingJobs(record) || optimisticPendingIntentTypes.length > 0) && (
                <CompanyResearchLoadingNotice
                  title="企業情報を詳しく調査しています。"
                  description="画面を移動しても、あとから履歴で確認できます。"
                />
              )}
              <AdditionalActionsPanel
                loadingAction={loadingAction}
                jobs={jobs}
                startedIntentTypes={startedIntentTypes}
                optimisticPendingIntentTypes={optimisticPendingIntentTypes}
                onCreateJob={requestAdditionalAnalysis}
              />
              <DeepResearchJobsPanel jobs={jobs} />
            </div>
          ) : (
            <div className="flex min-h-[320px] items-center justify-center rounded-[1.5rem] border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              企業調査履歴を取得できませんでした。
            </div>
          )}
        </section>
      </div>
      <CompanyResearchSelfUrlModal
        intentType={pendingSelfCompanyIntent}
        onClose={() => setPendingSelfCompanyIntent(null)}
        onSubmit={submitSelfCompanyUrl}
      />
    </main>
  );
}
